import { useCallback } from 'react';
import { create } from 'zustand';
import { useNotification } from '@libs/snackbar';
import { ShiftRecurrent } from '@libs/models/shifts/series';
import { getShiftSeries } from '@libs/api/shifts/getShift';
import { parseError } from '@libs/api/errors';
import { Analytics } from '@libs/analytics';
import {
  assignShift,
  editShiftSeries,
  EditShiftSeriesRequestData,
  publishShifts,
  terminate,
  unassignShift,
} from '@libs/api/shifts';

type State = {
  loading: boolean;
  drawerOpen: boolean;
  seriesId: string;
  initiated: boolean;
  data: ShiftRecurrent | null;
  load: (id: string) => Promise<void>;
  openShiftSeriesDrawer: (id: string) => Promise<void>;
  closeShiftSeriesDrawer: () => void;
  saveSeries: (id: string, shift: EditShiftSeriesRequestData) => Promise<void>;
  terminate: (id: string, reason: string) => Promise<void>;
  publish: (id: string) => Promise<void>;
  /**
   * @deprecated
   */
  assignShift: (shiftId: string, userId: string) => Promise<void>;
  /**
   * @deprecated
   */
  unassignShift: (shiftId: string, data: { reason: string; reasonComment: string }) => Promise<void>;
  assignSubmitting: boolean;
};

export const useShiftSeriesEditStore = create<State>()((set, get) => ({
  loading: false,
  drawerOpen: false,
  initiated: false,
  seriesId: '',
  data: null,
  assignSubmitting: false,
  load: async (id) => {
    set({ loading: true });
    try {
      const resp = await getShiftSeries(id);
      set({
        loading: false,
        data: {
          ...resp.data,
          id: resp.data.id,
          externalId: resp.data.externalId,
          department: resp.data.department.id,
          subDepartment: resp.data.subDepartment?.id ?? null,
          speciality: resp.data.speciality?.id ?? null,
          staffingType: resp.data.staffingType,
          qualifications: resp.data.staffNurseQualificationFilters,
          assignees: resp.data.staff.map((s) => ({
            id: s.id,
            name: [s.firstName, s.lastName].filter(Boolean).join(' '),
            avatar: s.imageURL,
          })),
          isShared: !!resp.data.isShared,
          isPublished: !!resp.data.isPublished,
          isTerminated: !!resp.data.isTerminated,
          isArchived: !!resp.data.isArchive,
          canShare: !!resp.data.canShare,
          isUrgent: !!resp.data.isUrgent,
          allowedMultipleAssignees: !!resp.data.canHasMultipleAssignee,
          events: resp.data.events.map((event) => ({
            id: event.id,
            startDate: event.startDate.dateTime,
            endDate: event.endDate.dateTime,
            timeZone: event.startDate.timeZone,
            status: event.status,
            assignee: event.staff
              ? {
                  id: event.staff.id,
                  name: [event.staff.firstName, event.staff.lastName].filter(Boolean).join(' '),
                  avatar: event.staff.imageURL,
                }
              : null,
            qualifications: resp.data.staffNurseQualificationFilters,
          })),
          locationId: resp.data.locationId,
          tags: resp.data.tags || [],
          notes: [
            {
              id: 'shiftNote',
              title: 'Note',
              value: resp.data.note || '',
            },
          ],
        },
      });
    } finally {
      set({ loading: false, initiated: true });
    }
  },
  openShiftSeriesDrawer: async (id) => {
    set({ seriesId: id, drawerOpen: true });
    await get().load(id);
  },
  closeShiftSeriesDrawer: () => {
    set({ drawerOpen: false, data: null, seriesId: '' });
  },
  saveSeries: async (id, series) => {
    await editShiftSeries(id, series);
  },
  terminate: async (id, reason) => {
    await terminate(id, reason);
  },
  publish: async (id) => {
    await publishShifts([id]);
  },
  /**
   * @deprecated
   */
  assignShift: async (shiftId: string, userId: string) => {
    set({ assignSubmitting: true });
    try {
      await assignShift(shiftId, userId);
      Analytics.track('Shift: Assigned', {
        shift_id: shiftId,
      });
    } finally {
      set({ assignSubmitting: false });
    }
  },
  /**
   * @deprecated
   */
  unassignShift: async (shiftId: string, data) => {
    set({ assignSubmitting: true });
    try {
      await unassignShift(shiftId, data.reason, data.reasonComment);
      Analytics.track('Shift: Unassigned', {
        shift_id: shiftId,
        unassignment_reason: data.reason,
      });
    } finally {
      set({ assignSubmitting: false });
    }
  },
}));

export function useSeriesDrawerState() {
  const { showErrorIntl } = useNotification();
  const open = useShiftSeriesEditStore((state) => state.openShiftSeriesDrawer);
  const close = useShiftSeriesEditStore((state) => state.closeShiftSeriesDrawer);

  return [
    useCallback(
      async (id: string) => {
        try {
          await open(id);
        } catch (error) {
          showErrorIntl(parseError(error).message);
          throw error;
        }
      },
      [open, showErrorIntl],
    ),
    close,
  ];
}
